import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Navigate, useNavigate, useParams } from "react-router";
import DropdownElement from "../Misc/Dropdown";
import FetchingDataAnimation from "../Preload/FetchingDataAnimation";
import { ConsumptionBar, Customer, Subscription } from "../Types";
import { ConsumptionSites } from "../App";
import Graph from "./Graph";
import "./index.css";
import config from "../config";

type Props = {
  customer: Customer;
  subscriptions: Subscription[];
  daily: ConsumptionSites;
  monthly: ConsumptionSites;
  isFetchingData: boolean;
};

export type FilterObject = {
  year?: number;
  month?: number;
};

export default function Overview({ customer, subscriptions, daily, monthly, isFetchingData }: Props) {
  const navigate = useNavigate();

  const params = useParams<{
    siteId: string;
    scope: "monthly" | "daily";
    filter: string;
  }>();

  //  Hantera gärna att ingen data finns här!

  let graphData: ConsumptionBar[] =
    params.scope === "daily"
      ? daily[Number(params.siteId)]
      : monthly[Number(params.siteId)];
  if (!graphData) { graphData = []; }
  const filter = new Date(params.filter || new Date());

  const graphDataYears: number[] = monthly[Number(params.siteId)]?.reduce(
    (previous, bar) => {
      return bar.totalVolume === 0 || previous.includes(bar.date.getFullYear())
        ? previous
        : [...previous, bar.date.getFullYear()];
    },
    []
  );

  const graphDataMonths: number[] = monthly[Number(params.siteId)]?.reduce(
    (previous, bar) => {
      if (
        bar.totalVolume === 0 ||
        bar.date.getFullYear() !== filter.getFullYear()
      ) {
        return previous;
      }
      return previous.includes(bar.date.getMonth() + 1)
        ? previous
        : [...previous, bar.date.getMonth() + 1];
    },
    []
  );

  graphData = graphData.filter((bar) => {
    if (bar.date.getFullYear() !== filter.getFullYear()) return false;
    if (params.scope === "daily") {
      return bar.date.getMonth() === filter.getMonth();
    }
    return true;
  });

  if (graphData.length) {
    const earliestDate = graphData.reduce((earliest, bar) => {
      return earliest > bar.date ? bar.date : earliest;
    }, graphData[0].date);

    const latestDate = graphData.reduce((latest, bar) => {
      return latest < bar.date ? bar.date : latest;
    }, graphData[0].date);

    if (filter < earliestDate || filter > latestDate || !params.filter) {
      return (
        <Navigate
          to={`/dashboard/${params.siteId}/${
            params.scope || "monthly"
          }/${latestDate.getFullYear()}-${String(
            latestDate.getMonth() + 1
          ).padStart(2, "0")}`}
          replace
        />
      );
    }
  }

  const months = [
    "Januari",
    "Februari",
    "Mars",
    "April",
    "Maj",
    "Juni",
    "Juli",
    "Augusti",
    "September",
    "Oktober",
    "November",
    "December",
  ];

  // Välj källa mellan daglig och månatlig info
  // Välj site från lista, om bara en välj den och göm picker
  // Year-filter, går att klicka på en månad och då se alla dagar
  // månad, kwh, snitt
  // Month-filter, går inte att klicka på
  // dag, kwh, snitt

  return (
    <div className="overview-container">
      {isFetchingData && !graphData.length ? (
        <FetchingDataAnimation />
      ) : (
        <Graph
          navigate={(e) =>
            navigate(
              `/dashboard/${params.siteId}/daily/${
                new Date(e.label).getFullYear() +
                "-" +
                String(new Date(e.label).getMonth() + 1).padStart(2, "0")
              }`
            )
          }
          data={graphData.map((bar) => {
            return {
              label:
                params.scope === "daily"
                  ? bar.date.toLocaleDateString("sv-se")
                  : bar.date.toLocaleString("sv-se", { month: "long" }) +
                    " " +
                    bar.date.getFullYear(),
              description: `Snitt ${Math.round(
                bar.totalVolume / bar.amountOfAvg
              )}kWh`,
              volume: bar.totalVolume,
            };
          })}
          roof={0}
        />
      )}
      <h2
        className="site-adress"
        style={{
          fontWeight: 500,
          marginTop: 15,
          marginBottom: 0,
          textTransform: "capitalize",
          wordBreak: "break-all",
          marginRight: 30,
        }}
      >
        {subscriptions
          ?.find((subscription) => subscription.id === Number(params.siteId))
          ?.adress.toLowerCase()}
        <FontAwesomeIcon
          style={{ fontSize: 20, marginLeft: 20 }}
          icon={faChevronRight}
        />
        <DropdownElement
          extraClass="site-adress-container"
          onChange={(e) => navigate(`/dashboard/${e.target.value}/monthly/`)}
          menuList={subscriptions.map((subscription) => {
            return {
              label: subscription.adress,
              value: subscription.id,
              selected: subscription.id === Number(params.siteId),
            };
          })}
        />
      </h2>
      {params.scope === "daily" ? (
        <div
          className="backwards-container"
          onClick={() =>
            navigate(
              `/dashboard/${params.siteId}/monthly/${
                new Date(params.filter).getFullYear() +
                "-" +
                String(new Date(params.filter).getMonth() + 1).padStart(2, "0")
              }`
            )
          }
        >
          Tillbaka till årsvyn
        </div>
      ) : null}

      <p
        className="prompt-to-hover"
        style={{ color: config.colors.primaryMuted, fontWeight: 400 }}
      >
        Håll med muspekaren över grafen för att se mer information
      </p>
      {graphDataYears ? 
      <DropdownElement
      onChange={(e) =>
        navigate(`/dashboard/${params.siteId}/monthly/${e.target.value}`)
      }
      menuList={graphDataYears.map((year) => {
        return {
          label: String(year),
          selected: filter.getFullYear() === year,
          value: year,
        };
      })}
    /> : null
    }
    { graphDataMonths ? 
      <DropdownElement
        placeholder="Månad"
        onChange={(e) =>
          navigate(
            `/dashboard/${params.siteId}/daily/${
              filter.getFullYear() +
              "-" +
              String(e.target.value).padStart(2, "0")
            }`
          )
        }
        menuList={graphDataMonths.map((month) => {
          return {
            label: months[month - 1],
            selected: filter.getMonth() === month - 1 && params.scope === "daily",
            value: month,
          };
        })}
      /> : null }
    </div>
  );
}
